/**
 * Der Sync-Reiter: Check-in von Gerät zu Gerät.
 *
 * Über den Cloud-Ordner wandern zwei Dateien: die Tauchgänge und Stammdaten in
 * `tauchlogbuch-daten.json`, die Unterlagen verschlüsselt in
 * `tauchlogbuch-checkin.json`. Getrennt, damit eine Notizänderung nicht den
 * Geheimtext mitüberträgt. Tiefenprofile bleiben draussen — 95 Prozent der
 * Datenmenge, die sich nie ändert; sie kommen einmalig über das Bundle.
 *
 * Der Datei-Weg unten trägt weiterhin nur den Check-in; für die Tauchgänge gibt
 * es den Abzug im Reiter „Laden".
 *
 * Drei Wege, dieselbe Schnittstelle: Datei, Dropbox, Google Drive. Der Abzug
 * enthält Geheimtext; auf dem zweiten Gerät wird dasselbe Passwort eingegeben,
 * der Schlüssel wandert nie mit.
 *
 * Der Zugriffsschlüssel des Anbieters liegt in `sessionStorage` und endet mit
 * dem Tab. Der App-Key beziehungsweise die Client-ID ist kein Geheimnis und
 * bleibt in `localStorage`, damit man ihn nicht jedes Mal neu einträgt.
 */

import { holeGrabsteine, holeTresor, liste, setzeTresor } from '../checkin.js';
import { SPEICHER, alleAus, ersetze } from '../db.js';
import { DATEINAME as DATEN_DATEI, gleicheDatenAb } from '../datensync.js';
import { ANBIETER, codeAusAbfrage, dateiAdapter, dateiZiel, stelleBinaeresHer,
  tokenAusAnker, ZUSTAND } from '../speicher.js';
import { baueAbzug, fasseZusammen, fuehreZusammen, geraeteKennung } from '../sync.js';
import { eigenerCheckinstand, gleicheCheckinAb, schreibeSaetze } from '../checkinsync.js';
import { BEREICHE, baueSicherung, beschreibe, pruefeSicherung,
  zusammenfassung } from '../sicherung.js';

export const SCHLUESSEL_ANBIETER = 'tauchlogbuch.anbieter';
const SCHLUESSEL_ID = 'tauchlogbuch.clientid';

/**
 * Die eingebauten Schluessel — beim Bauen hineingelegt, nicht eingetippt.
 *
 * Ein "App key" (Dropbox) oder eine "Application (client) ID" (Microsoft,
 * Google) ist **kein Geheimnis**: Sie sagt dem Anbieter nur, welche App gerade
 * fragt, und steht bei jeder Anmeldung ohnehin sichtbar in der Adresszeile.
 * Deshalb darf sie im oeffentlichen Build stehen — und deshalb kommt sie aus
 * GitHub-*Variablen*, nicht aus Secrets.
 *
 * Das Gegenteil war der Fehler: Der Schluessel lag allein in localStorage, also
 * je Geraet. Ein neues Telefon hiess abtippen — fuer einen Wert, der sich nie
 * aendert und den niemand verstecken muss.
 */
export const EINGEBAUT = {
  dropbox: import.meta.env?.VITE_CLIENTID_DROPBOX ?? '',
  drive: import.meta.env?.VITE_CLIENTID_DRIVE ?? '',
  onedrive: import.meta.env?.VITE_CLIENTID_ONEDRIVE ?? '',
};

/**
 * Welcher Schluessel gilt — und woher er kommt.
 *
 * Der eigene schlaegt den eingebauten, damit ein Fork die eigene Registrierung
 * nehmen kann, ohne den Build anzufassen. Ein leeres Feld ist deshalb keine
 * Loeschung, sondern eine Rueckkehr zum eingebauten; die Oberflaeche sagt das,
 * sonst waere das Feld eine Falle.
 */
export function schluessel(anbieter, einstellungen, eingebaut = EINGEBAUT) {
  const eigen = (einstellungen.getItem(`${SCHLUESSEL_ID}.${anbieter}`) ?? '').trim();
  if (eigen) return { wert: eigen, herkunft: 'eigen' };
  const fest = (eingebaut[anbieter] ?? '').trim();
  if (fest) return { wert: fest, herkunft: 'eingebaut' };
  return { wert: '', herkunft: 'fehlt' };
}

/**
 * Was unter dem Schluesselfeld steht.
 *
 * Ein leeres Feld hat je nach Lage drei verschiedene Bedeutungen — nichts
 * noetig, uebersteuert, oder es fehlt tatsaechlich etwas. Ohne diesen Satz
 * sieht man dem Feld keine davon an.
 */
export function schluesselHinweis(name, herkunft, hatEingebaut = false) {
  if (herkunft === 'eingebaut') {
    return `Der Schlüssel für ${name} ist eingebaut — hier ist nichts einzutragen. `
      + 'Das Feld übersteuert ihn nur, wenn du eine eigene Registrierung nimmst.';
  }
  if (herkunft === 'eigen') {
    return hatEingebaut
      ? 'Es gilt dein eigener Schlüssel von diesem Gerät. Das Feld leeren stellt '
        + 'den eingebauten wieder her.'
      : 'Es gilt dein eigener Schlüssel — er liegt auf diesem Gerät und muss auf '
        + 'jedem weiteren neu eingetragen werden.';
  }
  return `Ohne Schlüssel führt der Weg über ${name} nicht weiter: Die App muss dort `
    + 'einmal registriert sein. Sichern als Datei geht auch ohne.';
}
/**
 * Wo das Zugriffsrecht liegt — und warum nicht mehr in der Sitzung.
 *
 * Es lag in `sessionStorage` und endete mit dem Browser. Gedacht war das als
 * Vorsicht; in der Praxis hiess es, dass „angemeldet" nur galt, solange der
 * Reiter offen war. Wer am Rechner die App aufschlug, war nicht verbunden,
 * ohne es zu merken, und die Ausweise vom Telefon kamen nie an.
 *
 * Der Gegenwert war klein: Ein Dropbox-Recht laeuft ohnehin nach wenigen
 * Stunden ab, und die Ausweise liegen seit dem Umbau im Klartext — ein Recht
 * in der Sitzung schuetzte also nichts, was daneben nicht offen laege. Aus
 * „bei jedem Browserstart" wird damit „wenn das Recht abgelaufen ist".
 *
 * **Ein abgelaufenes Recht muss weg.** Solange es die Sitzung wegraeumte, war
 * das gleichgueltig. Jetzt raeumt es `vergissRecht()` weg, sobald ein Anbieter
 * mit 401 antwortet — sonst behauptet die Oberflaeche „verbunden", waehrend
 * jeder Zugriff scheitert.
 */
export const SCHLUESSEL_TOKEN = 'tauchlogbuch.token';
export const SCHLUESSEL_ZULETZT = 'tauchlogbuch.sync.zuletzt';
export const SCHLUESSEL_SICHERUNG = 'tauchlogbuch.sicherung.zuletzt';

/** Die Sicherung liegt neben den Sync-Dateien, nicht in ihnen. */
export const SICHERUNGS_DATEI = 'tauchlogbuch-sicherung.json';

export const SCHLUESSEL_SICHERUNGSZIEL = 'tauchlogbuch.sicherungsziel';

/**
 * Wohin die Sicherung geht — Vorgabe ist die Datei, die immer geht.
 *
 * Bewusst unabhängig vom Anbieter des Abgleichs: Wer über Drive abgleicht,
 * will seine Sicherung vielleicht trotzdem auf einer Festplatte haben. Ein
 * unbekannter Wert fällt auf `lokal` zurück statt auf einen Anbieter, der
 * eine Anmeldung verlangt.
 */
export function gewaehltesSicherungsziel(einstellungen = globalThis.localStorage) {
  const ziel = einstellungen.getItem(SCHLUESSEL_SICHERUNGSZIEL) || 'lokal';
  return ziel === 'lokal' || ANBIETER[ziel] ? ziel : 'lokal';
}

/** Die Rückleitung muss beim Anbieter genau so eingetragen sein. */
export function umleitungsAdresse(ort = globalThis.location) {
  return `${ort.origin}${ort.pathname}`;
}

/** Wo eine Meldung aus dem Anmeldeweg auf die Sync-Ansicht wartet. */
const MELDUNG = 'tauchlogbuch.sync.meldung';

/**
 * Welcher Anbieter den Anmeldeweg begonnen hat.
 *
 * Der Rückweg fragte bisher, welcher Anbieter *gerade gewählt* ist — nicht,
 * mit welchem der Weg begann. Wer während der Anmeldung umschaltet (oder einen
 * alten Rückweg im Reiter liegen hat), liess damit das Recht des einen
 * Anbieters beim anderen ablegen. Seit dem Umbau auf zwei verschiedene Flüsse
 * fällt das sofort auf: Ein Dropbox-Code landete beim Drive-Adapter, der gar
 * keinen Tokentausch mehr hat, und die Meldung sprach von Adressankern statt
 * von einem Anmeldeweg, der nicht zusammenpasst.
 */
const ANMELDUNG = 'tauchlogbuch.anmeldung.anbieter';

/**
 * Rückkehr aus dem impliziten Fluss — muss vor dem Router laufen.
 *
 * Google legt das Zugriffsrecht in den Adressanker, und dort führt diese App
 * ihre eigenen Pfade. Wer das nicht auseinanderhält, landet nach der Anmeldung
 * in der Liste, während das Recht ungelesen in der Adresse steht.
 *
 * Deshalb wird hier zuerst gefragt, ob im Anker eine Anmeldung steckt. Steht
 * dort keine, kommt `false` zurück und der Router macht weiter wie immer.
 * Andernfalls wird das Recht abgelegt, die Adresse gesäubert und auf den
 * Sync-Reiter umgeleitet, wo die Meldung erscheint.
 */
export function nimmAnkerEntgegen(
  ort = globalThis.location,
  sitzung = globalThis.sessionStorage,
  einstellungen = globalThis.localStorage,
  verlauf = globalThis.history,
) {
  const ergebnis = tokenAusAnker(ort.hash, sitzung.getItem(ZUSTAND));
  if (!ergebnis) return false;
  sitzung.removeItem(ZUSTAND);

  // Wie beim Code-Fluss: der Anbieter, mit dem der Weg begann.
  const wahl = sitzung.getItem(ANMELDUNG) ?? gewaehlterAnbieter(einstellungen);
  sitzung.removeItem(ANMELDUNG);
  if (ergebnis.token) {
    einstellungen.setItem(`${SCHLUESSEL_TOKEN}.${wahl}`, ergebnis.token);
    sitzung.setItem(MELDUNG, `Mit ${ANBIETER[wahl].name} verbunden.`);
  } else {
    sitzung.setItem(MELDUNG, `Anmeldung gescheitert: ${ergebnis.fehler}`);
  }
  // Das Recht darf nicht in der Adresse stehen bleiben — es landete sonst im
  // Verlauf des Browsers und in jedem Lesezeichen.
  verlauf.replaceState(null, '', `${umleitungsAdresse(ort)}#/sync`);
  return true;
}

/** Der gewaehlte Anbieter — als Name, nicht als Adapter. */
export function gewaehlterAnbieter(einstellungen = globalThis.localStorage) {
  const wahl = einstellungen.getItem(SCHLUESSEL_ANBIETER) ?? 'dropbox';
  return ANBIETER[wahl] ? wahl : 'dropbox';
}

/** Steht ein Zugriffsrecht bereit? */
export function istVerbunden(einstellungen = globalThis.localStorage) {
  return Boolean(einstellungen.getItem(`${SCHLUESSEL_TOKEN}.${gewaehlterAnbieter(einstellungen)}`));
}

/**
 * Ein Recht wegwerfen, das nicht mehr taugt.
 *
 * Aufgerufen dort, wo ein Zugriff mit 401 scheitert. Danach sagt die
 * Oberflaeche „nicht verbunden" und fuehrt mit einem Tipp zur Anmeldung —
 * statt weiter zu behaupten, alles sei in Ordnung.
 */
export function vergissRecht(anbieter = null, einstellungen = globalThis.localStorage) {
  const wahl = anbieter ?? gewaehlterAnbieter(einstellungen);
  einstellungen.removeItem(`${SCHLUESSEL_TOKEN}.${wahl}`);
}

/**
 * Derselbe Anbieter, andere Datei.
 *
 * Zwei Dateien im selben Ordner: die Ausweise verschluesselt, die Tauchgaenge
 * daneben. So uebertraegt eine Notizaenderung nicht den Check-in-Geheimtext.
 */
export function datenAdapter(
  einstellungen = globalThis.localStorage, sitzung = globalThis.sessionStorage,
) {
  const wahl = gewaehlterAnbieter(einstellungen);
  return ANBIETER[wahl].baue({
    clientId: schluessel(wahl, einstellungen).wert,
    token: einstellungen.getItem(`${SCHLUESSEL_TOKEN}.${wahl}`),
    umleitung: umleitungsAdresse(),
    dateiname: DATEN_DATEI,
    bereich: 'dives',
  });
}

/**
 * Der Adapter fuer die Ausweise — die Vorgabedatei des Anbieters.
 *
 * Steht hier und nicht als Verschluss in `ansichtSync`, weil der Check-in-
 * Reiter denselben Weg braucht: Wer dort etwas aendert, soll es gesichert
 * bekommen, ohne erst in diesen Reiter zu wechseln.
 */
export function checkinAdapter(
  einstellungen = globalThis.localStorage, sitzung = globalThis.sessionStorage,
) {
  const wahl = gewaehlterAnbieter(einstellungen);
  return ANBIETER[wahl].baue({
    clientId: schluessel(wahl, einstellungen).wert,
    token: einstellungen.getItem(`${SCHLUESSEL_TOKEN}.${wahl}`),
    umleitung: umleitungsAdresse(),
  });
}

/**
 * Derselbe Anbieter, dritte Datei.
 *
 * Neben `tauchlogbuch-daten.json` (Abgleich) und `tauchlogbuch-checkin.json`
 * (Ausweise) liegt die Sicherung als eigene Datei. Getrennt, weil sie etwas
 * anderes ist: Der Abgleich schreibt bei jeder Notizänderung, die Sicherung
 * schreibt selten und dafür alles.
 */
export function sicherungsZiel(
  einstellungen = globalThis.localStorage, sitzung = globalThis.sessionStorage,
) {
  const ziel = gewaehltesSicherungsziel(einstellungen);
  // Eigenes Format, eigene Prüfung — sonst weist das Ziel die Sicherung ab,
  // weil es den Abzug des Abgleichs erwartet.
  if (ziel === 'lokal') {
    return dateiZiel({ dateiname: SICHERUNGS_DATEI, pruefe: pruefeSicherung });
  }
  return ANBIETER[ziel].baue({
    clientId: schluessel(ziel, einstellungen).wert,
    token: einstellungen.getItem(`${SCHLUESSEL_TOKEN}.${ziel}`),
    umleitung: umleitungsAdresse(),
    dateiname: SICHERUNGS_DATEI,
    pruefe: pruefeSicherung,
  });
}

/** Alles einsammeln, was auf diesem Gerät liegt. */
export async function sammleSicherung(db, geraet) {
  const stand = {
    erzeugt: new Date().toISOString(),
    geraet,
    tresor: await holeTresor(db),
    // holeGrabsteine() liest die des Check-ins aus dem Tresor — nicht den
    // Merkzettel gelöschter Sätze. Der steckt als Speicher in BEREICHE.
    checkinGrabsteine: await holeGrabsteine(db),
  };
  for (const bereich of BEREICHE) stand[bereich] = await alleAus(db, SPEICHER[bereich]);
  return baueSicherung(stand);
}

/**
 * Eine Sicherung zurückholen — ersetzend, nicht ergänzend.
 *
 * Ein Stand ist ein Stand: Was hier liegt und dort fehlt, muss verschwinden,
 * sonst käme ein gelöschter Tauchgang durch die Sicherung zurück. Deshalb
 * `ersetze()` je Speicher und nicht `schreibe()`.
 *
 * Die Reihenfolge ist die von `BEREICHE`: Tauchgänge vor ihren Profilen, damit
 * ein Abbruch dazwischen keine Messpunkte ohne Tauchgang hinterlässt.
 */
export async function spieleSicherungEin(db, daten, melde = () => {}) {
  pruefeSicherung(daten);
  for (const bereich of BEREICHE) {
    const saetze = Array.isArray(daten[bereich]) ? daten[bereich] : [];
    melde(`${bereich}: ${saetze.length} …`);
    await ersetze(db, SPEICHER[bereich], saetze);
  }
  if (daten.tresor) await setzeTresor(db, daten.tresor);
  await setzeCheckinGrabsteine(db, daten.checkinGrabsteine ?? []);
  return zusammenfassung(daten);
}

/**
 * Die Grabsteine des Check-ins zurückschreiben.
 *
 * Sie liegen im Tresor-Speicher unter ihrem eigenen Schlüssel, so wie der
 * Abgleich sie ablegt. Ohne sie käme eine gelöschte Unterlage beim nächsten
 * Abgleich vom anderen Gerät zurück.
 */
function setzeCheckinGrabsteine(db, liste) {
  return new Promise((erfuellen, ablehnen) => {
    const t = db.transaction(SPEICHER.tresor, 'readwrite');
    t.objectStore(SPEICHER.tresor).put(liste, 'grabsteine');
    t.oncomplete = () => erfuellen();
    t.onerror = () => ablehnen(t.error);
  });
}

export function ansichtSync(db) {
  const wurzel = document.createElement('section');
  wurzel.className = 'sync';
  const einstellungen = globalThis.localStorage;
  const sitzung = globalThis.sessionStorage;

  function aktuellerAdapter(gewuenscht = null) {
    const wahl = gewuenscht ?? einstellungen.getItem(SCHLUESSEL_ANBIETER) ?? 'dropbox';
    const anbieter = ANBIETER[wahl] ?? ANBIETER.dropbox;
    return {
      wahl,
      anbieter,
      adapter: anbieter.baue({
        clientId: schluessel(wahl, einstellungen).wert,
        token: einstellungen.getItem(`${SCHLUESSEL_TOKEN}.${wahl}`),
        umleitung: umleitungsAdresse(),
      }),
    };
  }

  async function gleicheAb(adapter, melde) {
    melde(`${adapter.name} wird gelesen …`);
    const check = await gleicheCheckinAb(db, adapter, { stelleHer: stelleBinaeresHer });
    if (check.erstmalig) {
      melde(`Erster Abgleich: ${check.anzahl} Unterlagen abgelegt.`);
      return;
    }
    // Der Check-in ist abgeglichen; jetzt die Tauchgaenge.
    const daten = await gleicheDatenAb(db, datenAdapter(einstellungen, sitzung));
    const datenText = daten.erstmalig
      ? 'Tauchgänge erstmals abgelegt'
      : `Tauchgänge: ${fasseZusammen(daten.bericht)}`;
    melde(`Abgeglichen: ${fasseZusammen(check.bericht)}. ${datenText}.`);
    einstellungen.setItem(SCHLUESSEL_ZULETZT, new Date().toISOString());
  }

  async function zeichne(meldung = '') {
    const unterlagen = await liste(db);
    const tresor = await holeTresor(db);
    const { wahl, anbieter, adapter } = aktuellerAdapter();
    const eigenerSchluessel = (einstellungen.getItem(`${SCHLUESSEL_ID}.${wahl}`) ?? '').trim();
    const { wert: clientId, herkunft } = schluessel(wahl, einstellungen);
    const verbunden = Boolean(einstellungen.getItem(`${SCHLUESSEL_TOKEN}.${wahl}`));
    const zuletzt = einstellungen.getItem(SCHLUESSEL_ZULETZT);
    // Steht hier "vor drei Tagen", weiss man, dass etwas klemmt.
    const zuletztText = zuletzt
      ? `Zuletzt abgeglichen: ${new Date(zuletzt).toLocaleString('de-CH')}`
      : 'Noch nie abgeglichen.';
    const sicherungsziel = gewaehltesSicherungsziel(einstellungen);
    const zielLokal = sicherungsziel === 'lokal';
    const zielName = zielLokal ? 'Datei' : ANBIETER[sicherungsziel].name;
    const zielBereit = zielLokal
      || Boolean(einstellungen.getItem(`${SCHLUESSEL_TOKEN}.${sicherungsziel}`));
    const gesichert = einstellungen.getItem(SCHLUESSEL_SICHERUNG);
    const sicherungsText = gesichert
      ? `Zuletzt gesichert: ${new Date(gesichert).toLocaleString('de-CH')}`
      : 'Noch nie gesichert.';

    wurzel.innerHTML = `
      <header class="sync-kopf">
        <h1>Geräte-Sync</h1>
        <p class="sync-unterzeile">Überträgt Tauchgänge, Stammdaten und den
          Check-in auf ein anderes Gerät — die Ausweise verschlüsselt, wie sie hier
          liegen. Tiefenprofile bleiben draussen; sie kommen einmalig über das
          Bundle unter <a href="#/import">Laden</a>.</p>
      </header>
      <div class="kennzahlen">
        <div class="kennzahl"><span class="wert">${unterlagen.length}</span>
          <span class="label">Unterlagen</span></div>
        <div class="kennzahl"><span class="wert">${tresor ? 'ja' : 'nein'}</span>
          <span class="label">Check-in eingerichtet</span></div>
        <div class="kennzahl"><span class="wert mono-klein">${geraeteKennung().slice(0, 8)}</span>
          <span class="label">dieses Gerät</span></div>
      </div>

      <section class="block-weit">
        <h2>Über eine Datei</h2>
        <p class="fliess">Der Weg, der immer funktioniert: Abzug schreiben, Datei
          auf das andere Gerät bringen, dort einlesen. Ohne Konto, ohne Netz.</p>
        <div class="sync-knoepfe">
          <button type="button" class="knopf schreiben"${tresor ? '' : ' disabled'}>
            Abzug schreiben</button>
          <label class="knopf lesen">Abzug einlesen
            <input type="file" accept=".json,.gz" hidden></label>
        </div>
      </section>

      <section class="block-weit">
        <h2>Sicherungskopie</h2>
        <p class="fliess">Alles, was auf diesem Gerät liegt — <strong>samt
          Tiefenprofilen</strong> — als eine Datei im App-Ordner. Der Abgleich
          daneben hält zwei Geräte gleich; die Sicherung holt nach einem
          Verlust alles zurück. Zurückholen <strong>ersetzt</strong> den
          Bestand und fragt vorher.</p>
        <p class="sync-zuletzt">${sicherungsText}</p>
        <div class="sync-einstellungen">
          <label>Ziel
            <select class="sicherungsziel">
              <option value="lokal"${zielLokal ? ' selected' : ''}>Lokale Datei</option>
              ${Object.entries(ANBIETER).map(([schluessel, a]) =>
    `<option value="${schluessel}"${sicherungsziel === schluessel ? ' selected' : ''}>`
    + `${a.name}</option>`).join('')}
            </select>
          </label>
        </div>
        <div class="sync-knoepfe">
          <button type="button" class="knopf sichern"${zielBereit ? '' : ' disabled'}>
            ${zielLokal ? 'Als Datei sichern' : `Nach ${zielName} sichern`}</button>
          ${zielLokal
    ? `<label class="knopf ausdatei">Aus Datei holen
              <input type="file" accept=".json,.gz" hidden></label>`
    : `<button type="button" class="knopf zurueckholen"${zielBereit ? '' : ' disabled'}>
              Aus ${zielName} holen</button>`}
        </div>
        ${zielBereit ? '' : `<p class="hinweis">Erst unten mit ${zielName} verbinden — `
    + 'oder als Ziel die lokale Datei wählen, die immer geht.</p>'}
      </section>

      <section class="block-weit">
        <h2>Über einen Cloud-Ordner</h2>
        <p class="fliess">Jeder Anbieter legt die Datei in einem eigenen
          App-Ordner ab, der vom übrigen Konto getrennt ist. Dazu muss die App
          beim Anbieter registriert sein — daher der Schlüssel:
          ${anbieter.hinweis}. Er ist <strong>kein Geheimnis</strong>, sondern
          ein Namensschild: Er sagt nur, welche App fragt. Ist er beim Bauen
          hinterlegt, ist hier nichts einzutragen.</p>
        <div class="sync-einstellungen">
          <label>Anbieter
            <select class="anbieter">${Object.entries(ANBIETER).map(([wert, a]) =>
    `<option value="${wert}"${wert === wahl ? ' selected' : ''}>${a.name}</option>`).join('')}
            </select>
          </label>
          <label>Schlüssel
            <input class="clientid" value="${eigenerSchluessel}"
              placeholder="${herkunft === 'eingebaut' ? 'eingebaut' : 'noch nicht eingetragen'}"
              autocomplete="off" spellcheck="false"></label>
          <label>Rückleitung eintragen als
            <input class="umleitung" value="${umleitungsAdresse()}" readonly></label>
        </div>
        <p class="fliess leise">${schluesselHinweis(anbieter.name, herkunft, Boolean(EINGEBAUT[wahl]))}</p>
        <p class="sync-zuletzt">${zuletztText}</p>
        <div class="sync-knoepfe">
          <button type="button" class="knopf verbinden"${clientId ? '' : ' disabled'}>
            ${verbunden ? 'Neu verbinden' : `Mit ${anbieter.name} verbinden`}</button>
          <button type="button" class="knopf abgleichen"${verbunden ? '' : ' disabled'}>
            Jetzt abgleichen</button>
        </div>
        <p class="fliess leise">Der Anmeldeweg läuft über PKCE; die Rechnung dahinter
          ist gegen das Beispiel der Norm geprüft, ebenso Anfragen und Fehlerfälle
          beider Anbieter. <strong>Nicht geprüft ist der Verkehr mit dem echten
          Konto</strong> — dafür braucht es den Schlüssel oben.</p>
      </section>

      <p class="sync-meldung" role="status" aria-live="polite">${meldung}</p>`;

    const melde = (text) => { wurzel.querySelector('.sync-meldung').textContent = text; };

    wurzel.querySelector('.anbieter').addEventListener('change', (e) => {
      einstellungen.setItem(SCHLUESSEL_ANBIETER, e.target.value);
      zeichne();
    });
    wurzel.querySelector('.clientid').addEventListener('change', (e) => {
      const eingetippt = e.target.value.trim();
      einstellungen.setItem(`${SCHLUESSEL_ID}.${wahl}`, eingetippt);
      // Ein geleertes Feld loescht nicht, es faellt auf den eingebauten
      // zurueck. Wer das nicht liest, sucht sonst einen Schluessel, der da ist.
      zeichne(eingetippt
        ? 'Schlüssel gespeichert.'
        : (EINGEBAUT[wahl] ? 'Eigener Schlüssel entfernt — es gilt wieder der eingebaute.'
          : 'Schlüssel entfernt.'));
    });

    wurzel.querySelector('.verbinden').addEventListener('click', () => {
      try {
        // Vor dem Wechsel merken, wer den Weg beginnt — der Rückweg fragt
        // danach und nicht danach, was inzwischen im Auswahlfeld steht.
        sitzung.setItem(ANMELDUNG, wahl);
        adapter.anmelden();                    // die Seite wechselt zum Anbieter
      } catch (fehler) {
        melde(fehler.message);
      }
    });

    wurzel.querySelector('.abgleichen').addEventListener('click', async () => {
      try {
        await gleicheAb(adapter, melde);
        zeichne(wurzel.querySelector('.sync-meldung').textContent);
      } catch (fehler) {
        melde(`Nicht abgeglichen: ${fehler.message}`);
      }
    });

    /**
     * Die Sicherung als Datei — der Weg, der immer offensteht.
     *
     * Ohne ihn war auf dieser Seite nichts anklickbar, solange weder ein
     * Check-in eingerichtet noch eine Cloud verbunden war: Genau der Zustand,
     * in dem jemand seine Tauchgaenge zum ersten Mal sichern will.
     */
    wurzel.querySelector('.sicherungsziel').addEventListener('change', (e) => {
      einstellungen.setItem(SCHLUESSEL_SICHERUNGSZIEL, e.target.value);
      zeichne();
    });

    /**
     * Zurückholen — der gemeinsame Teil beider Wege.
     *
     * Aus der Datei und aus der Cloud kommt dasselbe Objekt; ab hier
     * unterscheidet sich nichts mehr. Zweimal geschrieben liefen die beiden
     * Wege auseinander, und die Rückfrage vor dem Überschreiben ist genau die
     * Stelle, an der das niemand merken würde.
     */
    async function holeZurueck(daten) {
      pruefeSicherung(daten);
      const frage = `${beschreibe(daten)}\n\nDas ersetzt den Bestand auf diesem `
        + 'Gerät vollständig. Fortfahren?';
      if (!globalThis.confirm(frage)) { melde('Abgebrochen, nichts geändert.'); return; }
      const z = await spieleSicherungEin(db, daten, melde);
      await zeichne(`Zurückgeholt: ${z.tauchgaenge} Tauchgänge, ${z.profile} Profile, `
        + `${z.plaetze} Plätze. Die Liste zeigt den Stand nach dem Neuladen.`);
    }

    wurzel.querySelector('.sichern').addEventListener('click', async () => {
      try {
        melde('Bestand wird eingesammelt …');
        const sicherung = await sammleSicherung(db, geraeteKennung());
        const z = zusammenfassung(sicherung);
        melde(zielLokal
          ? `${z.tauchgaenge} Tauchgänge, ${z.profile} Profile werden geschrieben …`
          : `${z.tauchgaenge} Tauchgänge, ${z.profile} Profile werden hochgeladen …`);
        const ergebnis = await sicherungsZiel(einstellungen, sitzung).schreib(sicherung);
        einstellungen.setItem(SCHLUESSEL_SICHERUNG, new Date().toISOString());
        await zeichne(`Gesichert: ${z.tauchgaenge} Tauchgänge, ${z.profile} Profile, `
          + `${z.messpunkte.toLocaleString('de-CH')} Messpunkte `
          + `(${(ergebnis.bytes / 1024 / 1024).toFixed(1)} MB)`
          + `${zielLokal ? ' — in den Downloads.' : ` nach ${zielName}.`}`);
      } catch (fehler) {
        melde(`Nicht gesichert: ${fehler.message}`);
      }
    });

    wurzel.querySelector('.ausdatei input')?.addEventListener('change', async (e) => {
      const datei = e.target.files?.[0];
      if (!datei) return;
      try {
        await holeZurueck(await sicherungsZiel(einstellungen, sitzung).lies(datei));
      } catch (fehler) {
        melde(`Nicht geladen: ${fehler.message}`);
      } finally {
        e.target.value = '';
      }
    });

    wurzel.querySelector('.zurueckholen')?.addEventListener('click', async () => {
      try {
        melde(`${zielName} wird gelesen …`);
        const fern = await sicherungsZiel(einstellungen, sitzung).lies();
        if (!fern) { melde(`Im App-Ordner von ${zielName} liegt keine Sicherung.`); return; }
        await holeZurueck(stelleBinaeresHer(fern));
      } catch (fehler) {
        melde(`Nicht zurückgeholt: ${fehler.message}`);
      }
    });

    wurzel.querySelector('.schreiben').addEventListener('click', async () => {
      const abzug = baueAbzug({ ...(await eigenerCheckinstand(db)), geraet: geraeteKennung() });
      const ergebnis = await dateiAdapter.schreib(abzug);
      melde(`${abzug.checkin.length} Unterlagen geschrieben `
        + `(${(ergebnis.bytes / 1024).toFixed(1)} KB, verschlüsselt).`);
    });

    wurzel.querySelector('.lesen input').addEventListener('change', async (ereignis) => {
      const datei = ereignis.target.files?.[0];
      if (!datei) return;
      try {
        const fern = await dateiAdapter.lies(datei);
        const lokal = await eigenerCheckinstand(db);
        const zusammen = fuehreZusammen(lokal, fern);
        await schreibeSaetze(db, zusammen.checkin, zusammen.grabsteine, zusammen.tresor);
        if (!lokal.tresor && fern.tresor) await setzeTresor(db, fern.tresor);
        zeichne(`Zusammengeführt: ${fasseZusammen(zusammen.bericht)}.`
          + (lokal.tresor && fern.tresor
            && String(lokal.tresor.salz) !== String(fern.tresor.salz)
            ? ' Achtung: Der Abzug stammt aus einem anderen Check-in-Bereich — '
              + 'zum Entsperren gilt weiterhin dein bisheriges Passwort.'
            : ''));
      } catch (fehler) {
        zeichne(`Nicht eingelesen: ${fehler.message}`);
      }
    });
  }

  /** Rückkehr vom Anbieter: `?code=…` steht in der Adresse. */
  async function pruefeRueckleitung() {
    const ergebnis = codeAusAbfrage(globalThis.location.search, sitzung.getItem(ZUSTAND));
    if (!ergebnis) return false;
    // Der Anbieter, mit dem der Weg begann — nicht der gerade gewählte.
    const { wahl, adapter, anbieter } = aktuellerAdapter(sitzung.getItem(ANMELDUNG));
    history.replaceState(null, '', `${umleitungsAdresse()}#/sync`);
    sitzung.removeItem(ZUSTAND);
    sitzung.removeItem(ANMELDUNG);
    if (ergebnis.fehler) {
      await zeichne(`Anmeldung gescheitert: ${ergebnis.fehler}`);
      return true;
    }
    try {
      // Anbieter ohne Tokentausch (Google) kommen über den Anker zurück, nicht
      // über die Abfrage. Landet trotzdem ein Code hier, stimmt etwas nicht —
      // und ein `undefined is not a function` waere die schlechteste Auskunft.
      if (typeof adapter.tausche !== 'function') {
        throw new Error(`${anbieter.name} gibt das Recht im Adressanker zurück, `
          + 'nicht als Code. Bitte den Anmeldeweg neu beginnen.');
      }
      const token = await adapter.tausche(ergebnis.code);
      einstellungen.setItem(`${SCHLUESSEL_TOKEN}.${wahl}`, token);
      await zeichne(`Mit ${anbieter.name} verbunden.`);
    } catch (fehler) {
      await zeichne(`Anmeldung gescheitert: ${fehler.message}`);
    }
    return true;
  }

  // Eine Meldung aus dem impliziten Rückweg wartet in der Sitzung — einmal
  // zeigen, dann wegräumen, sonst stünde sie bei jedem Besuch wieder da.
  const wartende = sitzung.getItem(MELDUNG);
  if (wartende) sitzung.removeItem(MELDUNG);

  pruefeRueckleitung().then((behandelt) => { if (!behandelt) zeichne(wartende ?? ''); });
  return wurzel;
}
